import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  createRiskCategory,
  updateRiskCategory,
  deleteRiskCategory,
  HVARiskCategory
} from 'services/hvarisk.service';

interface HVARiskCategoriesTableProps {
  categories: HVARiskCategory[];
  onCategoriesChange: () => Promise<void>;
}

const CategoriesChangeContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

const useCategoriesChange = () => {
  const context = useContext(CategoriesChangeContext);
  if (context === undefined) {
    throw new Error(
      'useCategoriesChange must be used within a CategoriesChangeProvider'
    );
  }
  return context;
};

type CategoryActionCellProps = {
  category: HVARiskCategory;
};

const CategoryActionCell = ({ category }: CategoryActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<HVARiskCategory | null>(null);
  const onCategoriesChange = useCategoriesChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCategory(null);
  };

  const handleShowEditModal = () => {
    setSelectedCategory(category);
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteRiskCategory(category.id);
      console.log(`Category with ID: ${category.id} deleted successfully`);
      await onCategoriesChange();
    } catch (error) {
      console.error(`Error deleting Category with ID: ${category.id}`, error);
      alert(
        'Error: This category may have associated risk elements and cannot be deleted.'
      );
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedCategory) {
      try {
        await updateRiskCategory(selectedCategory.id, selectedCategory);
        await onCategoriesChange();
        handleCloseEditModal();
      } catch (error) {
        alert('Error updating category: ' + error);
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setSelectedCategory(prev => (prev ? { ...prev, [name]: value } : null));
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Risk Category?
          <br />
          <strong>Note:</strong> Categories with associated risk elements cannot
          be deleted.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Risk Category</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={selectedCategory?.name || ''}
                onChange={handleInputChange}
                required
                maxLength={100}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Display Order</Form.Label>
              <Form.Control
                type="number"
                name="displayorder"
                value={selectedCategory?.displayorder || 1}
                onChange={handleInputChange}
                required
                min={1}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Category
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const riskcategoriesTablecolumns: ColumnDef<HVARiskCategory>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'name',
    header: 'Category Name',
    meta: {
      headerProps: {
        style: { width: '5%' },
        className: 'px-1 fs-10 white-space-nowrap'
      },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <CategoryActionCell category={original} />;
    },
    meta: {
      headerProps: { style: { width: '5%' } },
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'displayorder',
    header: 'Display Order',
    meta: {
      headerProps: {
        style: { width: '10%' },
        className: 'px-1 fs-10 text-center'
      },
      cellProps: { className: 'px-1 text-center' }
    }
  },
  {
    accessorKey: 'filled',
    enableSorting: false,
    header: '',
    cell: () => {
      return true;
    },
    meta: {
      headerProps: { style: { width: '75%' } },
      cellProps: { className: 'text-body-tertiary' }
    }
  }
];

export const HVARiskCategoriesTable: React.FC<HVARiskCategoriesTableProps> = ({
  categories,
  onCategoriesChange
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCategory, setNewCategory] = useState<Partial<HVARiskCategory>>({
    name: '',
    displayorder: 1
  });

  const table = useAdvanceTable({
    data: categories,
    columns: riskcategoriesTablecolumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewCategory({ name: '', displayorder: 1 });
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewCategory(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await createRiskCategory(newCategory);
      handleCloseAddModal();
      onCategoriesChange();
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error creating category: ' + error);
    }
  };

  return (
    <CategoriesChangeContext.Provider value={onCategoriesChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Categories"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Add Category
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Risk Category</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newCategory.name || ''}
                onChange={handleInputChange}
                required
                maxLength={100}
                placeholder="e.g., Compliance Risks"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Display Order</Form.Label>
              <Form.Control
                type="number"
                name="displayorder"
                value={newCategory.displayorder || 1}
                onChange={handleInputChange}
                required
                min={1}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Category
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </CategoriesChangeContext.Provider>
  );
};
